import { z } from 'zod';
import {
  booleanSchema,
  createModel,
  currencySchema,
  partySchema,
  statusCommonSchema,
  temperatureTypeSchema,
  tripLoadTypeSchema,
  tripTypeSchema,
  vehicleTypesSchema,
} from '../../common';
import { nonZeroPriceSchema, objectIdSchema, quantitySchema } from '../../validation';
import { routeRegionSchema } from './route.region.model';
import { UTagSharedSchema } from '../utag';
import { routeOrdersSchema } from './route.orders.model';

/**
 * `Route db model` — the canonical pricing document for a lane.
 *
 * A `Route` holds a single neutral price for a lane (origin → destination + vehicle +
 * temperature + load type). Hero is a network, not a broker, so there is no
 * "platform" route; prices belong to the parties. Both sides of the trade are
 * captured optionally:
 *
 *   - `seller=A, buyer=B`     → directed bilateral price (`isOwnRoute=false`).
 *                                With `agreementId` set, it is contract-bound.
 *   - `seller=A, buyer=null`  → A's own published rate / rate card (`isOwnRoute=true`).
 *   - `seller=null, buyer=B`  → B's own default / budget rate (`isOwnRoute=true`).
 *   - `seller=null, buyer=null` → REJECTED at write time.
 *
 * The {@link PricingResolutionService} consumes this collection to resolve a
 * single price for any (buyer, seller, lane, creator) tuple.
 */
export const RouteSchema = createModel({
  /** status of the route */
  status: statusCommonSchema,

  /** the currency of the Route */
  currency: currencySchema,

  /**
   * the party offering capacity for sale.
   *
   * Required when `buyer` is empty. Together with `buyer`, exactly one of the
   * following four shapes must hold (see model JSDoc above).
   *
   * Carries a full {@link partySchema} snapshot built by
   * {@link PartyResolverService.makeParty}. For a `target: 'client'`
   * seller, `ids[]` includes the bridged `profile_id` / `org_id` derived
   * from `Client.linkedParty` (and `Profile.linkedOrgId` when set) so
   * downstream resolvers can match real Hero Org ids without an extra
   * join. See `docs/relations-v2/02-data-model.md` §9.
   */
  seller: partySchema.optional(),

  /**
   * the party paying for capacity.
   *
   * Required when `seller` is empty. A buyer-only Route represents that
   * party's own default / budget rate for the lane (e.g. "I will pay up to
   * 120 for any Riyadh→Jeddah flatbed").
   *
   * Same {@link partySchema} shape and bridging semantics as `seller`.
   */
  buyer: partySchema.optional(),

  /**
   * Optional link to a contractual wrapper. When set:
   *   - both `seller` AND `buyer` MUST be present (directed Route);
   *   - both sides MUST match `Agreement.parties[]` (order-independent);
   *   - the Route's lifecycle follows the Agreement status
   *     (`expired`/`terminated`/`cancelled` → Route is archived by
   *     `AgreementService.onStatusChange`).
   */
  agreementId: objectIdSchema.optional(),

  /** the trip type of the load such as `one_way` or `round_trip` */
  tripType: tripTypeSchema,

  /** goods or container */
  loadType: tripLoadTypeSchema,

  /**
   * the vehicle types this price applies to.
   *
   * An array so a single Route can cover multiple equivalent vehicles
   * (e.g. `['flatbed', 'curtain_side']` when the same lane is priced the
   * same for either trailer type). Vehicle overlap on the same
   * `(seller, buyer, lane)` is REJECTED at write time —
   * sellers must remove the conflicting vehicle from the existing Route
   * before adding a new one that overlaps.
   */
  vehicles: z.array(vehicleTypesSchema).min(1),

  /** the destinations that the route will go to such as Riyadh, Jeddah, Dammam.
   *
   * The first destination is always the starting point, and last element in the array is stop point.
   */
  destinations: z.array(routeRegionSchema).min(2),

  /** the distance in `KM` between the center of `from` and the center of `to` region */
  distance: quantitySchema,

  /** the temperature type for the price and vehicle type.
   *
   * the reason it's an array is because sometimes we can give the same price for vehicle for frozen and chilled shipment.
   */
  temperatureType: z.array(temperatureTypeSchema).min(1),

  /**
   * the single neutral price for this lane BEFORE any fees or tax.
   *
   * No more carrier/customer split — the broker margin is recovered per-leg
   * at the contract layer (`sell.realizedSubtotal − buy.realizedSubtotal`).
   */
  price: nonZeroPriceSchema,

  /**
   * Derived flag. Server-computed on every write — callers MUST NOT set it
   * directly (overwritten in `RouteService.normalize`).
   *
   *   isOwnRoute = !seller || !buyer
   *
   *   - `true`  → open/own price (seller's rate card OR buyer's default rate)
   *   - `false` → directed bilateral price (both sides set)
   *
   * Exists purely to make hot queries trivial:
   *   `Route.find({ 'seller._id': X, isOwnRoute: true })`  → X's rate card
   *   `Route.find({ 'buyer._id': Y,  isOwnRoute: true })`  → Y's defaults
   *   `Route.find({ 'seller._id': X, 'buyer._id': Y, isOwnRoute: false })` → directed
   */
  isOwnRoute: booleanSchema,

  /**
   * Expected orders volume on this lane.
   *
   * - For an own/open Route (no counter-party): the seller's or buyer's
   *   forecast for the lane.
   * - For a directed Route (both sides set): the volume the two parties
   *   agreed on at contract time.
   */
  expectedOrders: routeOrdersSchema,

  /** the actual orders for the route ... this will be empty unless we have enough actual data */
  actualOrders: routeOrdersSchema.optional(),

  UTags: z.array(UTagSharedSchema),
});

/** `Route db model` */
export type Route = z.infer<typeof RouteSchema.model>;
export type RouteData = z.infer<typeof RouteSchema.data>;
export type RoutePartial = z.infer<typeof RouteSchema.partial>;
